/**
 * Compact region flag/emoji pill used by every NewsPulse card surface.
 * Reads `event.source_country` — a value produced by the backend
 * `_hydrate_cards` helper that prefers `event.geo_country` and falls
 * back to `_AGGREGATOR_REGION_MAP` for Google News regional feeds.
 *
 * Renders nothing when `source_country` is null/undefined so cards
 * without a region signal stay clean.
 */
import { Globe2 } from "lucide-react";

// Curated flag/emoji lookup. ISO-2 country codes get their flag;
// aggregator region tags get a continent glyph.
const REGION_META = {
  IN: { flag: "🇮🇳", label: "India" },
  US: { flag: "🇺🇸", label: "United States" },
  GB: { flag: "🇬🇧", label: "United Kingdom" },
  CA: { flag: "🇨🇦", label: "Canada" },
  AU: { flag: "🇦🇺", label: "Australia" },
  JP: { flag: "🇯🇵", label: "Japan" },
  CH: { flag: "🇨🇭", label: "Switzerland" },
  IT: { flag: "🇮🇹", label: "Italy" },
  ES: { flag: "🇪🇸", label: "Spain" },
  FR: { flag: "🇫🇷", label: "France" },
  DE: { flag: "🇩🇪", label: "Germany" },
  BR: { flag: "🇧🇷", label: "Brazil" },
  MX: { flag: "🇲🇽", label: "Mexico" },
  CN: { flag: "🇨🇳", label: "China" },
  RU: { flag: "🇷🇺", label: "Russia" },
  ZA: { flag: "🇿🇦", label: "South Africa" },
  NG: { flag: "🇳🇬", label: "Nigeria" },
  KE: { flag: "🇰🇪", label: "Kenya" },
  EG: { flag: "🇪🇬", label: "Egypt" },
  IL: { flag: "🇮🇱", label: "Israel" },
  SA: { flag: "🇸🇦", label: "Saudi Arabia" },
  AE: { flag: "🇦🇪", label: "UAE" },
  PK: { flag: "🇵🇰", label: "Pakistan" },
  BD: { flag: "🇧🇩", label: "Bangladesh" },
  LK: { flag: "🇱🇰", label: "Sri Lanka" },
  ID: { flag: "🇮🇩", label: "Indonesia" },
  PH: { flag: "🇵🇭", label: "Philippines" },
  TH: { flag: "🇹🇭", label: "Thailand" },
  VN: { flag: "🇻🇳", label: "Vietnam" },
  KR: { flag: "🇰🇷", label: "South Korea" },
  SG: { flag: "🇸🇬", label: "Singapore" },
  // Aggregator regions have no ISO flag — use continent glyphs.
  WORLD:          { emoji: "🌍", label: "World" },
  EU:             { emoji: "🇪🇺", label: "Europe" },
  AF:             { emoji: "🌍", label: "Africa" },
  ME:             { emoji: "🕌", label: "Middle East" },
  LATAM:          { emoji: "🌎", label: "Latin America" },
  APAC:           { emoji: "🌏", label: "Asia-Pacific" },
  GLOBAL_BIZ:     { emoji: "📈", label: "Global business" },
  GLOBAL_TECH:    { emoji: "💻", label: "Global tech" },
  GLOBAL_HEALTH:  { emoji: "🩺", label: "Global health" },
  GLOBAL_CLIMATE: { emoji: "🌱", label: "Global climate" },
  GLOBAL_SPORTS:  { emoji: "🏆", label: "Global sports" },
  GLOBAL_ENT:     { emoji: "🎬", label: "Global entertainment" },
  GLOBAL_SCI:     { emoji: "🔬", label: "Global science" },
};

export default function RegionChip({ event, size = "sm" }) {
  const code = event?.source_country;
  if (!code) return null;
  const meta = REGION_META[code];
  const glyph = meta?.flag || meta?.emoji;
  const label = meta?.label || code;
  const pad = size === "xs" ? "px-1.5 py-0" : "px-2 py-0.5";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full ${pad} text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100`}
      data-testid={`event-region-${event.event_id}-${code}`}
      title={`Region: ${label}`}
    >
      {glyph ? <span aria-hidden="true">{glyph}</span> : <Globe2 className="w-2.5 h-2.5" />}
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden">{code}</span>
    </span>
  );
}
